import { ReactNode, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Button, Icon } from '@malash-office/ui-components';
import { usePortalMe } from '@/api/portal';
import { useAppContext } from '@/context/AppContext';
import PortalLogoutDialog from './PortalLogoutDialog';

/**
 * The shell an external production customer sees.
 *
 * Deliberately not `DashboardLayout` with the menu filtered down. That layout is built from
 * `NAV_ITEMS`, and three of its top-level groups carry no permission at all — they would render for
 * a customer, and two of them point at routes that do not exist. Filtering a menu is a rule
 * somebody has to keep applying; a second shell with no menu in it cannot leak a link.
 *
 * What is here is what the requester listed: the organisation's name, a way to file an order, a way
 * back to the list, and a way out.
 */
const PortalLayout = ({ children }: { children: ReactNode }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAppContext();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { data: me } = usePortalMe();
  const [confirmLogout, setConfirmLogout] = useState(false);

  const organizationName = me?.customers[0]?.organizationName ?? '';
  const onList = location.pathname === '/portal';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box
        component="header"
        sx={{
          bgcolor: theme.palette.common.white,
          borderBottom: `1px solid ${theme.palette.divider}`,
          px: { xs: 2, md: 4 },
          py: 1.5
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '11px',
                bgcolor: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <Icon icon="cart" size="small" color="common.white" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{ fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {organizationName || 'פורטל לקוחות'}
              </Box>
              <Box
                sx={{ fontSize: 11.5, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                הזמנות פיתוח ורכש - המרכז לעזרי שליחות
              </Box>
            </Box>
          </Stack>

          {/*
            `fullWidth={false}` on both, and `flexShrink: 0` on the row.

            The design system's `Button` defaults to `fullWidth`, which is right inside a form and
            wrong in a header: the flex row hands it whatever is left after the organisation's name,
            and "הזמנה חדשה" came out as two lines inside a pill. Sizing to content and refusing to
            shrink keeps every label on one line at any width — including a long organisation name,
            which now truncates instead of squeezing the buttons.
          */}
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
            {onList ? (
              <Button variant="contained" fullWidth={false} onClick={() => navigate('/portal/new')}>
                הזמנה חדשה
              </Button>
            ) : (
              <Button variant="outlined" fullWidth={false} onClick={() => navigate('/portal')}>
                {isSmallScreen ? 'ההזמנות' : 'ההזמנות שלי'}
              </Button>
            )}
            <Button variant="text" fullWidth={false} onClick={() => setConfirmLogout(true)}>
              יציאה
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* The same padding `DashboardLayout` gives a page, because the portal's screens sit on
          `PlanningPageFrame`, whose negative margin cancels exactly that. */}
      <Box component="main" sx={{ p: { xs: 2, md: 3 } }}>
        {children}
      </Box>

      <PortalLogoutDialog open={confirmLogout} onClose={() => setConfirmLogout(false)} onConfirm={logout} />
    </Box>
  );
};

export default PortalLayout;
